class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Re-open after ATC/remove paint: always ensure active class (iOS can miss the
    // first transition if we no-op when already .active from a prior open).
    this.classList.remove('active');
    // Force reflow so the next active add retriggers the slide-in on Safari.
    void this.offsetWidth;

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    try {
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    } catch (error) {
      console.error(error);
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;

    let sections = parsedState?.sections || {};
    let cartDrawerHtml = sections['cart-drawer'];

    // ATC sometimes returns incomplete/stale section HTML (common on iOS Safari).
    // Prefer a fresh cart-drawer section whenever the payload cannot paint items.
    const responseLooksPaintable =
      cartDrawerHtml && this.sectionHasCartDrawer(cartDrawerHtml) && this.sectionHasCartItems(cartDrawerHtml);

    if (!responseLooksPaintable) {
      cartDrawerHtml = await this.fetchCartDrawerSectionHtml();
      if (cartDrawerHtml) {
        sections = { ...sections, 'cart-drawer': cartDrawerHtml };
      }
    }

    const painted = this.paintCartDrawerSection(cartDrawerHtml);
    if (!painted) {
      // Last resort: refetch once more from /cart before giving up.
      cartDrawerHtml = await this.fetchCartDrawerSectionHtml();
      if (!this.paintCartDrawerSection(cartDrawerHtml)) {
        console.error('Cart drawer: failed to paint section HTML');
        return;
      }
    }

    const empty = !this.querySelector('.cart-item');
    this.classList.toggle('is-empty', empty);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    const iconHtml = sections['cart-icon-bubble'];
    if (iconHtml) {
      const iconRoot = document.getElementById('cart-icon-bubble');
      if (iconRoot) {
        const iconInner = this.getSectionInnerHTML(iconHtml, '.shopify-section') ?? this.getSectionInnerHTML(iconHtml);
        if (iconInner != null) iconRoot.innerHTML = iconInner;
      }
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.onclick = null;
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  sectionHasCartDrawer(html) {
    if (!html) return false;
    return /id=["']CartDrawer["']/.test(html) || /class=["'][^"']*drawer__inner/.test(html);
  }

  sectionHasCartItems(html) {
    if (!html) return false;
    return /class=["'][^"']*\bcart-item\b/.test(html) || /id=["']CartDrawer-Item-/.test(html);
  }

  paintCartDrawerSection(html) {
    if (!html) return false;
    const cartDrawerRoot = document.getElementById('CartDrawer');
    if (!cartDrawerRoot) return false;

    // Prefer swapping #CartDrawer contents; fall back to .drawer__inner only.
    const fromId = this.getSectionInnerHTML(html, '#CartDrawer');
    if (fromId != null && fromId !== '') {
      cartDrawerRoot.innerHTML = fromId;
      return true;
    }

    const fromInner = this.getSectionDOM(html, '.drawer__inner');
    const liveInner = cartDrawerRoot.querySelector('.drawer__inner');
    if (fromInner && liveInner) {
      liveInner.replaceWith(fromInner);
      return true;
    }

    return false;
  }

  fetchCartDrawerSectionHtml() {
    // Always fetch against /cart so the section renders with the live cart,
    // not a page template that may omit or stale-cache drawer markup.
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .catch((error) => {
        console.error(error);
        return null;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = selector ? doc.querySelector(selector) : doc.body;
    return root ? root.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
